/**
 * CallForTestDialog - the GM asks somebody to roll.
 *
 * Who (one, several, or the whole party), what they test and how hard it
 * is; optionally someone to test against, with a subject and difficulty of
 * their own; whether the result is the GM's alone; and what the test
 * carries beyond its roll - helpers, a clock its degrees fill, a kept
 * board entry a success shows. It builds a
 * `TestCall` (engine/narrative/callForTest) and hands it over: who is
 * prompted, who is rolled at once and where the result goes are the
 * caller's (`hooks/useTestCalls`).
 *
 * Lit plates for one-of-several, rockers for on/off, like every dialog of
 * the modes work.
 */
import { useMemo, useState } from 'react';
import PromptRows from './PromptRows';
import { promptDefaults } from '../../engine/roster/actedAs';
import common from '../../styles/common.module.css';
import styles from '../../styles/CallForTest.module.css';
import Rocker from '../controls/Rocker';
import useFocusTrap from '../../hooks/useFocusTrap.js';
import useModalBackdrop from '../../hooks/useModalBackdrop';
import useEscapeKey from '../../hooks/useEscapeKey.js';
import { CHARACTERISTIC_TEST_LABELS, DIFFICULTY_LADDER, SKILL_TEST_DEFS } from '../../engine/characteristicTests';
import { MAX_ASSISTS } from '../../engine/narrative/assist';

const subjectOf = (value) => {
  const [kind, id] = value.split(':');
  return { kind, id };
};
const DEFAULT_SUBJECT = 'skill:awareness';

/** What is tested: every skill, then every characteristic. */
function SubjectPicker({ label, testId, value, onChange }) {
  return (
    <label className={styles.field}>
      <span className={styles.caption}>{label}</span>
      <select className={styles.select} data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}>
        <optgroup label="Skills">
          {SKILL_TEST_DEFS.map((s) => (
            <option key={s.id} value={`skill:${s.id}`}>
              {s.label}
            </option>
          ))}
        </optgroup>
        <optgroup label="Characteristics">
          {Object.entries(CHARACTERISTIC_TEST_LABELS).map(([id, text]) => (
            <option key={id} value={`characteristic:${id}`}>
              {text}
            </option>
          ))}
        </optgroup>
      </select>
    </label>
  );
}

function DifficultyPicker({ testId, value, onChange }) {
  return (
    <label className={styles.field}>
      <span className={styles.caption}>Difficulty</span>
      <select
        className={styles.select}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      >
        {DIFFICULTY_LADDER.map((step) => (
          <option key={step.id} value={step.mod}>
            {step.label} {step.mod >= 0 ? `+${step.mod}` : step.mod}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Who is asked: click to tick, and one press for the whole party when it is named. */
function WhoList({ roster, who, setWho, party }) {
  const toggle = (id) => setWho(who.includes(id) ? who.filter((x) => x !== id) : [...who, id]);
  return (
    <div className={styles.field}>
      <div className={styles.captionRow}>
        <span className={styles.caption}>Who</span>
        {party.length > 0 && (
          <button type="button" className={styles.link} data-testid="call-who-party" onClick={() => setWho(party)}>
            The whole party
          </button>
        )}
      </div>
      <div className={styles.list} role="listbox" aria-multiselectable="true" aria-label="Who is asked">
        {roster.map((c) => (
          <button
            key={c.id}
            type="button"
            role="option"
            aria-selected={who.includes(c.id)}
            className={styles.row}
            data-testid={`call-who-${c.id}`}
            onClick={() => toggle(c.id)}
          >
            {c.name}
          </button>
        ))}
      </div>
    </div>
  );
}

/** The opposing side: one person, what they test, how hard it is for them. */
function AgainstFields({ roster, against, setAgainst }) {
  const set = (patch) => setAgainst({ ...against, ...patch });
  return (
    <div className={styles.against}>
      <label className={styles.field}>
        <span className={styles.caption}>Against</span>
        <select
          className={styles.select}
          data-testid="call-against-who"
          value={against.who}
          onChange={(e) => set({ who: e.target.value })}
        >
          <option value="">Choose who</option>
          {roster.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
      <SubjectPicker
        label="They test"
        testId="call-against-subject"
        value={against.subject}
        onChange={(subject) => set({ subject })}
      />
      <DifficultyPicker
        testId="call-against-difficulty"
        value={against.difficulty}
        onChange={(difficulty) => set({ difficulty })}
      />
    </div>
  );
}

/** The call the form describes, or null while it names nobody (or opposes nobody it said it would). */
function callOf({ who, subject, difficulty, opposed, against, secret, assists, fills, reveals, prompt }) {
  if (who.length === 0 || (opposed && !against.who)) return null;
  return {
    who,
    // Who is prompted, only where the GM changed it from the defaults (the hook knows them too).
    ...(prompt ? { prompt } : {}),
    subject: subjectOf(subject),
    difficulty,
    against: opposed ? { who: against.who, subject: subjectOf(against.subject), difficulty: against.difficulty } : null,
    secret,
    // Each rider only when it says something: a plain call stays a plain call.
    ...(assists ? { assists } : {}),
    ...(fills ? { fills } : {}),
    ...(reveals ? { reveals } : {}),
  };
}

const HELPERS = Array.from({ length: MAX_ASSISTS + 1 }, (_, n) => n);

/** One of the form's optional riders, as a select over ids with "none" first. */
function RiderSelect({ label, testId, none, value, options, onChange }) {
  return (
    <label className={styles.field}>
      <span className={styles.caption}>{label}</span>
      <select className={styles.select} data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{none}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * What the test carries beyond its roll: how many help, the clock its
 * degrees fill (an extended test), and the kept board entry a success
 * shows. The last two are offered only when there is a clock, or a kept
 * entry, to offer.
 */
function Riders({ form, set, clocks, kept, assistBonus }) {
  return (
    <div className={styles.pair}>
      <label className={styles.field}>
        <span className={styles.caption}>Helping</span>
        <select
          className={styles.select}
          data-testid="call-assists"
          value={form.assists}
          onChange={(e) => set({ assists: Number(e.target.value) })}
        >
          {HELPERS.map((n) => (
            <option key={n} value={n}>
              {n === 0 ? 'Nobody' : `${n} (+${n * assistBonus})`}
            </option>
          ))}
        </select>
      </label>
      {clocks.length > 0 && (
        <RiderSelect
          label="Its degrees fill"
          testId="call-fills"
          none="No clock"
          value={form.fills}
          options={clocks.map((c) => ({ id: c.id, label: c.name }))}
          onChange={(fills) => set({ fills })}
        />
      )}
      {kept.length > 0 && (
        <RiderSelect
          label="A success shows"
          testId="call-reveals"
          none="Nothing"
          value={form.reveals}
          options={kept.map((e) => ({ id: e.id, label: e.title ? `${e.title}: ${e.text}` : e.text }))}
          onChange={(reveals) => set({ reveals })}
        />
      )}
    </div>
  );
}

const EMPTY_AGAINST = { who: '', subject: DEFAULT_SUBJECT, difficulty: 0 };

/**
 * @param {object} props
 * @param {{id: string, name: string}[]} props.roster
 * @param {string[]} [props.party] - the party's combatant ids, when the party is named
 * @param {{id: string, name: string}[]} [props.clocks] - the clocks an extended test may fill
 * @param {{id: string, title: string, text: string, visible: boolean}[]} [props.entries] - the board; a success may show a kept one
 * @param {number} [props.assistBonus] - what each helper adds (the table's ruling)
 * @param {{uid: string, name: string}[]} [props.people] - the accounts at the table (v9.5.3)
 * @param {Record<string, string[]>} [props.actedBy] - combatantId -> the accounts that acted as them
 * @param {Record<string, string>} [props.actingBy] - account -> the combatant acted as now
 * @param {string|null} [props.gmUid] - the GM's own account, never prompted
 * @param {(call: import('../../engine/narrative/callForTest').TestCall) => void} props.onCall
 * @param {() => void} props.onClose
 */
export default function CallForTestDialog({
  roster,
  party = [],
  clocks = [],
  entries = [],
  assistBonus = 10,
  people = [],
  actedBy = {},
  actingBy = {},
  gmUid = null,
  onCall,
  onClose,
}) {
  const trapRef = useFocusTrap(true);
  const backdrop = useModalBackdrop(onClose, trapRef);
  useEscapeKey(true, onClose); // Escape cancels, as every other dialog does (2026-10-23)
  const [form, setForm] = useState({
    who: [],
    subject: DEFAULT_SUBJECT,
    difficulty: 0,
    opposed: false,
    against: EMPTY_AGAINST,
    secret: false,
    assists: 0,
    fills: '',
    reveals: '',
    /** Rows the GM changed; absent rows take the defaults. */
    prompted: {},
  });
  const kept = entries.filter((e) => !e.visible);
  const set = (patch) => setForm((was) => ({ ...was, ...patch }));
  const defaults = useMemo(
    () => promptDefaults(form.who, actedBy, actingBy, gmUid),
    [form.who, actedBy, actingBy, gmUid],
  );
  const prompt = { ...defaults, ...form.prompted };
  const changed = Object.keys(form.prompted).length > 0;
  const call = callOf({ ...form, prompt: changed ? prompt : null });
  return (
    <div className={common.modal} onClick={backdrop.onBackdropClick} role="presentation">
      <div
        className={common.modalContent}
        ref={backdrop.boxRef}
        role="dialog"
        aria-modal="true"
        aria-label="Call for a test"
        data-testid="call-test-dialog"
      >
        <h3 className={styles.title}>Call for a test</h3>
        <WhoList roster={roster} who={form.who} setWho={(who) => set({ who })} party={party} />
        {people.length > 0 && (
          <PromptRows
            who={form.who}
            roster={roster}
            people={people}
            prompt={prompt}
            onChange={(next) => set({ prompted: { ...form.prompted, ...next } })}
          />
        )}
        <div className={styles.pair}>
          <SubjectPicker
            label="Tests"
            testId="call-subject"
            value={form.subject}
            onChange={(subject) => set({ subject })}
          />
          <DifficultyPicker
            testId="call-difficulty"
            value={form.difficulty}
            onChange={(difficulty) => set({ difficulty })}
          />
        </div>
        <div className={styles.rockers}>
          <Rocker
            label="Against someone"
            testId="call-opposed"
            on={form.opposed}
            onToggle={() => set({ opposed: !form.opposed })}
          />
          <Rocker
            label="Only the GM sees the result"
            testId="call-secret"
            on={form.secret}
            onToggle={() => set({ secret: !form.secret })}
          />
        </div>
        {form.opposed && (
          <AgainstFields
            roster={roster.filter((c) => !form.who.includes(c.id))}
            against={form.against}
            setAgainst={(against) => set({ against })}
          />
        )}
        <Riders form={form} set={set} clocks={clocks} kept={kept} assistBonus={assistBonus} />
        <div className={styles.footer}>
          <button type="button" className={common.btnSecondary} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={common.btnPrimary}
            data-testid="call-go"
            disabled={!call}
            onClick={() => {
              onCall(call);
              onClose();
            }}
          >
            Ask
          </button>
        </div>
      </div>
    </div>
  );
}
